import { GetServerSideProps } from 'next';
import { ParsedUrlQuery } from 'querystring';

import { EmbedParam } from 'constants/embed.constants';

export interface EmbedConfig {
  /** Rendered inside someone else's page: an iframe or the app's WebView. */
  isEmbedded: boolean;
  header: boolean;
  /** Controls that need a session, e.g. favourite and bookmark. */
  auth: boolean;
  cookiesPopup: boolean;
}

/** The site itself: everything on. */
const NOT_EMBEDDED: EmbedConfig = {
  isEmbedded: false,
  header: true,
  auth: true,
  cookiesPopup: true,
};

const flag = (value: string | Array<string> | undefined) =>
  value === '1' || value === 'true';

/**
 * An embed starts from nothing and the host turns on what it wants, so a
 * mistyped param leaves a plain lineup rather than leaking chrome or tracking.
 */
export const parseEmbedParams = (query: ParsedUrlQuery): EmbedConfig => {
  if (!flag(query[EmbedParam.Embed])) return NOT_EMBEDDED;

  return {
    isEmbedded: true,
    header: flag(query[EmbedParam.Header]),
    auth: flag(query[EmbedParam.Auth]),
    cookiesPopup: flag(query[EmbedParam.Cookies]),
  };
};

/** Reads the embed config off a URL string. `/embed/x` is the same as `/x?embed=1`. */
export const parseEmbedParamsFromUrl = (url: string): EmbedConfig => {
  const [path = '', search] = url.split?.('?') ?? [];
  const query = Object.fromEntries(new URLSearchParams(search));

  if (/^\/(?:[a-z]{2}\/)?embed\//.test(path)) {
    query[EmbedParam.Embed] = '1';
  }

  return parseEmbedParams(query);
};

/** The switches a link has to carry to stay in the same embed. */
export const getEmbedQuery = (embed: EmbedConfig) => {
  const params = new URLSearchParams();

  if (embed.header) params.set(EmbedParam.Header, '1');
  if (embed.auth) params.set(EmbedParam.Auth, '1');
  if (embed.cookiesPopup) params.set(EmbedParam.Cookies, '1');

  return params.toString();
};

/**
 * Resolved on the server so the first HTML is already chrome-less. Left to the
 * client, the host would see our navigation flash inside their page.
 */
export const getEmbedServerSideProps: GetServerSideProps<{
  embed: EmbedConfig;
}> = (context) =>
  Promise.resolve({ props: { embed: parseEmbedParams(context.query) } });
